// Libraries
import { Card, Flex, Text } from '@chakra-ui/react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
// Components
import { BearingLogo, LockIcon } from '@atoms/index';
import { ChangePasswordForm } from '@organisms/index';
// Utils
import { getQueryParam } from '@helpers/index';
import text from '@translations/en.json';
// Types
import { ChangePasswordStatus, ChangePasswordTextsConfig } from './ChangePasswordCard.type';

/**
 * Represents the configuration object for ChangePasswordTexts.
 */
const textConfig: ChangePasswordTextsConfig = {
  [ChangePasswordStatus.INITIAL_LOGIN]: {
    /**
     * Generates the title for the INITIAL_LOGIN status.
     * @param name - The user's name.
     * @returns The generated title string.
     */
    title: (name) => `${text.change_password_welcome_title}, ${name}!`,
    /**
     * The subtitle for the INITIAL_LOGIN status.
     */
    subtitle: text.change_password_welcome_subtitle,
  },
  [ChangePasswordStatus.CHANGE]: {
    /**
     * Generates the title for the CHANGE status.
     * @returns The generated title string.
     */
    title: () => text.change_password_change_title,
    /**
     * The subtitle for the CHANGE status.
     */
    subtitle: text.change_password_change_subtitle,
  },
};

/**
 * Renders the ChangePasswordCard component that allows the user to change the password using the provided link.
 * @component
 * @returns {ReactElement} DatePicker component.
 */
export const ChangePasswordCard = () => {
  const { query, asPath } = useRouter();
  const { data: userData } = useSession();
  const status = (getQueryParam(query, 'status') ?? ChangePasswordStatus.CHANGE) as ChangePasswordStatus;
  const username = getQueryParam(query, 'username') ?? userData?.user?.username ?? '';
  const givenName = getQueryParam(query, 'given_name') ?? userData?.user?.givenName ?? '';
  const familyName = getQueryParam(query, 'family_name') ?? userData?.user?.familyName ?? '';
  const confirmationCode = asPath.match(/&confirmation_code=(.*)/)?.[1] ?? '';
  const fullName = `${givenName} ${familyName}`;
  const isChangeStatus = status === ChangePasswordStatus.CHANGE;

  return (
    <Card p={9} align='flex-start' gap={8} maxW='fit-content'>
      <Flex
        gap={4}
        w='full'
        align='center'
        direction={{ base: 'column', md: 'row' }}
        textAlign={{ base: 'center', md: 'left' }}
      >
        {isChangeStatus ? <LockIcon w={10} h={9} /> : <BearingLogo w={32} h={14} logoType='name' />}
        <Flex direction='column'>
          <Text as='h1' fontSize='hs' fontWeight='semibold'>
            {textConfig[status].title(fullName)}
          </Text>
          <Text mt={0} fontSize='m' display={{ base: 'none', md: 'inline-block' }}>
            {textConfig[status].subtitle}
          </Text>
        </Flex>
      </Flex>
      <Flex w='full' direction='column'>
        <Text mt={0} fontSize='m' mb={1} display={{ base: 'inline-block', md: 'none' }}>
          {textConfig[status].subtitle}
        </Text>
        <ChangePasswordForm username={username} confirmationCode={confirmationCode} status={status} />
      </Flex>
    </Card>
  );
};
